// src/services/memberService.js
// Enhanced Member Service for Goal Accountability Partners
// Handles authentication, dashboard data, check-ins, and real-time updates

class MemberService {
    constructor() {
      this.baseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/member-dashboard`;
      this.apiKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      // Enhanced state management
      this.cache = new Map();
      this.cacheTimestamps = new Map();
      this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
      this.retryAttempts = 3;
      this.retryDelay = 1000; // 1 second
      
      // Token management
      this.currentToken = null;
      this.tokenExpiry = null;
      this.refreshInterval = null;
      
      // Initialize token from URL or localStorage
      this.initializeToken();
    }
  
    // =====================================================
    // TOKEN MANAGEMENT
    // =====================================================
  
    initializeToken() {
      // First, try to get token from URL
      const urlToken = this.getTokenFromURL();
      if (urlToken) {
        this.setToken(urlToken);
        this.clearTokenFromURL();
        return urlToken;
      }
  
      // Fallback to localStorage (with expiry check)
      const storedData = localStorage.getItem('memberToken');
      if (storedData) {
        try {
          const { token, expiry } = JSON.parse(storedData);
          if (expiry && new Date().getTime() < expiry) {
            this.setToken(token);
            return token;
          } else {
            this.clearToken();
          }
        } catch (error) {
          console.warn('Invalid stored token data, clearing:', error);
          this.clearToken();
        }
      }
  
      return null;
    }
  
    setToken(token, expiryHours = 24) {
      this.currentToken = token;
      this.tokenExpiry = new Date().getTime() + (expiryHours * 60 * 60 * 1000);
      
      // Store in localStorage with expiry
      localStorage.setItem('memberToken', JSON.stringify({
        token,
        expiry: this.tokenExpiry
      }));
  
      console.log('🔑 Token set with expiry:', new Date(this.tokenExpiry));
    }
  
    getToken() {
      // Check if current token is expired
      if (this.tokenExpiry && new Date().getTime() > this.tokenExpiry) {
        console.log('🔑 Token expired, clearing');
        this.clearToken();
        return null;
      }
      return this.currentToken;
    }
  
    clearToken() {
      this.currentToken = null;
      this.tokenExpiry = null;
      localStorage.removeItem('memberToken');
      this.clearCache();
      
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    }
  
    isTokenValid() {
      const token = this.getToken();
      return token && this.tokenExpiry && new Date().getTime() < this.tokenExpiry;
    }
  
    // Helper to extract token from URL
    getTokenFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('token');
    }
  
    // Helper to clear token from URL without refresh
    clearTokenFromURL() {
      const url = new URL(window.location);
      url.searchParams.delete('token');
      window.history.replaceState({}, document.title, url.toString());
    }
  
    // =====================================================
    // ENHANCED API CALLS WITH RETRY LOGIC
    // =====================================================
  
    async callAPI(action, data = null, options = {}) {
      const { 
        useCache = false, 
        cacheKey = null,
        retries = this.retryAttempts,
        skipTokenCheck = false 
      } = options;
  
      // Check cache first (if enabled)
      if (useCache && cacheKey) {
        const cached = this.getCachedData(cacheKey);
        if (cached) {
          console.log(`📦 Using cached data for: ${action}`);
          return cached;
        }
      }
  
      // Validate token unless explicitly skipped
      if (!skipTokenCheck && !this.isTokenValid() && action !== 'request_access') {
        return {
          success: false,
          error: 'Session expired. Please request a new access link.',
          expired: true
        };
      }
  
      let attempt = 0;
      while (attempt < retries) {
        try {
          console.log(`🔑 Member API call: ${action} (attempt ${attempt + 1}/${retries})`);
  
          const response = await fetch(this.baseUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.apiKey}`
            },
            body: JSON.stringify({
              action,
              data
            })
          });
  
          const result = await response.json();
  
          if (response.ok && result.success) {
            console.log(`✅ Member API success: ${action}`);
            
            // Cache successful results if caching is enabled
            if (useCache && cacheKey && result.data) {
              this.setCachedData(cacheKey, result);
            }
            
            return result;
          } 
          
          // Handle specific error cases
          if (result.expired) {
            console.log('🔑 Token expired during API call');
            this.clearToken();
            return {
              success: false,
              error: 'Session expired. Please request a new access link.',
              expired: true
            };
          }
          
          // If it's a server error and we have retries left, try again
          if (response.status >= 500 && attempt < retries - 1) {
            console.warn(`⚠️ Server error for ${action}, retrying...`, result);
            attempt++;
            await this.sleep(this.retryDelay * attempt); // Exponential backoff
            continue;
          }
  
          console.error(`❌ Member API error: ${action}`, result);
          return {
            success: false,
            error: result.error || 'API call failed',
            expired: result.expired || false
          };
  
        } catch (error) {
          console.error(`❌ Member API network error: ${action}`, error);
          
          // If it's a network error and we have retries left, try again
          if (attempt < retries - 1) {
            attempt++;
            await this.sleep(this.retryDelay * attempt);
            continue;
          }
          
          return {
            success: false,
            error: 'Network error. Please check your connection and try again.'
          };
        }
      }
    }
  
    // =====================================================
    // CACHING SYSTEM
    // =====================================================
  
    setCachedData(key, data) {
      this.cache.set(key, data);
      this.cacheTimestamps.set(key, new Date().getTime());
    }
  
    getCachedData(key) {
      const timestamp = this.cacheTimestamps.get(key);
      if (!timestamp) return null;
  
      const age = new Date().getTime() - timestamp;
      if (age > this.cacheExpiry) {
        this.cache.delete(key);
        this.cacheTimestamps.delete(key);
        return null;
      }
  
      return this.cache.get(key);
    }
  
    clearCache() {
      this.cache.clear();
      this.cacheTimestamps.clear();
    }
  
    invalidateCache(pattern = null) {
      if (!pattern) {
        this.clearCache();
        return;
      }
  
      // Clear cache entries matching pattern
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
          this.cacheTimestamps.delete(key);
        }
      }
    }
  
    // =====================================================
    // CORE API METHODS (ENHANCED)
    // =====================================================
  
    // Request magic link access with enhanced validation
    async requestAccess(email) {
      if (!email || !this.isValidEmail(email)) {
        return {
          success: false,
          error: 'Please enter a valid email address'
        };
      }
  
      return await this.callAPI('request_access', { 
        email: email.toLowerCase().trim() 
      }, { 
        skipTokenCheck: true,
        retries: 2 // Fewer retries for access requests
      });
    }
  
    // Validate member token with automatic retry
    async validateToken(token = null) {
      const tokenToValidate = token || this.getToken();
      
      if (!tokenToValidate) {
        return {
          success: false,
          error: 'No access token available'
        };
      }
  
      const result = await this.callAPI('validate_token', { 
        token: tokenToValidate 
      }, { 
        skipTokenCheck: true 
      });
  
      // If validation successful, update our token
      if (result.success && token) {
        this.setToken(token);
      }
  
      return result;
    }
  
    // Get member dashboard data with caching
    async getDashboardData(forceRefresh = false) {
      const token = this.getToken();
      if (!token) {
        return {
          success: false,
          error: 'No access token available',
          expired: true
        };
      }
  
      // Clear cache if force refresh
      if (forceRefresh) {
        this.invalidateCache('dashboard');
      }
  
      return await this.callAPI('get_dashboard_data', { token }, {
        useCache: true,
        cacheKey: 'dashboard_data'
      });
    }
  
    // Submit weekly check-in with validation
    async submitCheckin(checkinData) {
      const token = this.getToken();
      if (!token) {
        return {
          success: false,
          error: 'No access token available',
          expired: true
        };
      }
  
      // Validate check-in data first
      const validation = this.validateCheckinData(checkinData);
      if (!validation.isValid) {
        return {
          success: false,
          error: 'Please fix the following issues:',
          errors: validation.errors
        };
      }
  
      const result = await this.callAPI('submit_checkin', { 
        token, 
        checkinData 
      });
  
      // Invalidate dashboard cache on successful check-in
      if (result.success) {
        this.invalidateCache('dashboard');
        this.invalidateCache('checkin_history');
      }
  
      return result;
    }
  
    // Get check-in history with caching
    async getCheckinHistory(limit = 10, forceRefresh = false) {
      const token = this.getToken();
      if (!token) {
        return {
          success: false,
          error: 'No access token available',
          expired: true
        };
      }
  
      if (forceRefresh) {
        this.invalidateCache('checkin_history');
      }
  
      return await this.callAPI('get_checkin_history', { 
        token, 
        limit 
      }, {
        useCache: true,
        cacheKey: `checkin_history_${limit}`
      });
    }
  
    // =====================================================
    // REAL-TIME UPDATES AND POLLING
    // =====================================================
  
    // Start auto-refresh for dashboard data
    startAutoRefresh(intervalMinutes = 5) {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
      }
  
      this.refreshInterval = setInterval(async () => {
        if (this.isTokenValid()) {
          console.log('🔄 Auto-refreshing dashboard data...');
          await this.getDashboardData(true);
        }
      }, intervalMinutes * 60 * 1000);
  
      console.log(`🔄 Auto-refresh started (every ${intervalMinutes} minutes)`);
    }
  
    // Stop auto-refresh
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
        console.log('🔄 Auto-refresh stopped');
      }
    }
  
    // Check for dashboard updates
    async checkForUpdates() {
      if (!this.isTokenValid()) return null;
  
      try {
        const result = await this.getDashboardData(true);
        return result.success ? result.data : null;
      } catch (error) {
        console.warn('Failed to check for updates:', error);
        return null;
      }
    }
  
    // =====================================================
    // ENHANCED UTILITY FUNCTIONS
    // =====================================================
  
    // Email validation
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }
  
    // Sleep utility for retry delays
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  
    // Enhanced check-in validation
    validateCheckinData(data) {
      const errors = [];
  
      // At least one field should be filled
      const hasContent = data.goals_met?.trim() || 
                        data.challenges_faced?.trim() || 
                        data.next_week_commitment?.trim() ||
                        data.mood_rating ||
                        data.progress_rating ||
                        data.time_invested_hours;
  
      if (!hasContent) {
        errors.push('Please fill in at least one field for your check-in');
      }
  
      // Validate ratings
      if (data.mood_rating && (data.mood_rating < 1 || data.mood_rating > 5)) {
        errors.push('Mood rating must be between 1 and 5');
      }
  
      if (data.progress_rating && (data.progress_rating < 1 || data.progress_rating > 5)) {
        errors.push('Progress rating must be between 1 and 5');
      }
  
      // Validate time invested
      if (data.time_invested_hours !== undefined && data.time_invested_hours !== null) {
        if (data.time_invested_hours < 0 || data.time_invested_hours > 168) {
          errors.push('Time invested must be between 0 and 168 hours per week');
        }
      }
  
      // Validate overall progress percentage
      if (data.overall_progress_percentage !== undefined && data.overall_progress_percentage !== null) {
        if (data.overall_progress_percentage < 0 || data.overall_progress_percentage > 100) {
          errors.push('Progress percentage must be between 0 and 100');
        }
      }
  
      // Validate text lengths
      if (data.goals_met && data.goals_met.length > 1000) {
        errors.push('Goals description is too long (max 1000 characters)');
      }
  
      if (data.challenges_faced && data.challenges_faced.length > 1000) {
        errors.push('Challenges description is too long (max 1000 characters)');
      }
  
      if (data.next_week_commitment && data.next_week_commitment.length > 1000) {
        errors.push('Next week commitment is too long (max 1000 characters)');
      }
  
      return {
        isValid: errors.length === 0,
        errors
      };
    }
  
    // Enhanced encouragement messages
    getEncouragementMessage(analytics) {
      if (!analytics) return '🎯 Keep working toward your goals!';
  
      const { current_streak, avg_progress_rating, improvement_trend, total_checkins } = analytics;
  
      // First-time user
      if (total_checkins === 0) {
        return '🌟 Welcome! Start your journey by submitting your first check-in.';
      }
  
      // Streak-based messages
      if (current_streak >= 8) {
        return `🔥 Incredible! ${current_streak} weeks of consistent progress. You're unstoppable!`;
      } else if (current_streak >= 4) {
        return `🚀 Amazing ${current_streak}-week streak! You're building excellent habits.`;
      } else if (current_streak >= 2) {
        return `💪 Great momentum with your ${current_streak}-week streak. Keep it going!`;
      }
  
      // Progress-based messages
      if (avg_progress_rating >= 4.5) {
        return `⭐ Outstanding work! Your ${avg_progress_rating}/5 average shows exceptional progress.`;
      } else if (avg_progress_rating >= 4) {
        return `🌟 Excellent progress! Your ${avg_progress_rating}/5 average is impressive.`;
      }
  
      // Trend-based messages
      if (improvement_trend === 'improving') {
        return '📈 You\'re on an upward trajectory! Recent check-ins show great improvement.';
      } else if (improvement_trend === 'declining') {
        return '💪 Every expert was once a beginner. Your accountability group believes in you!';
      }
  
      // Default positive message
      return '🎯 Every step forward counts. Stay consistent and celebrate small wins!';
    }
  
    // =====================================================
    // UI HELPER FUNCTIONS
    // =====================================================
  
    getMoodEmoji(rating) {
      const emojiMap = {
        1: '😞',
        2: '😐', 
        3: '🙂',
        4: '😊',
        5: '🤩'
      };
      return emojiMap[rating] || '😐';
    }
  
    getProgressEmoji(rating) {
      const emojiMap = {
        1: '🚫',
        2: '📉',
        3: '📊', 
        4: '📈',
        5: '🚀'
      };
      return emojiMap[rating] || '📊';
    }
  
    getMoodColor(rating) {
      if (!rating) return 'text-gray-500';
      
      const colorMap = {
        1: 'text-red-500',
        2: 'text-orange-500',
        3: 'text-yellow-500',
        4: 'text-green-500',
        5: 'text-purple-500'
      };
      return colorMap[rating] || 'text-gray-500';
    }
  
    getProgressColor(rating) {
      if (!rating) return 'text-gray-500';
      
      const colorMap = {
        1: 'text-red-600',
        2: 'text-orange-600',
        3: 'text-yellow-600',
        4: 'text-green-600',
        5: 'text-purple-600'
      };
      return colorMap[rating] || 'text-gray-500';
    }
  
    formatDate(dateString) {
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return dateString;
      }
    }
  
    formatLongDate(dateString) {
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        return dateString;
      }
    }
  
    getRelativeTime(dateString) {
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
        if (diffInDays === 0) return 'Today';
        if (diffInDays === 1) return 'Yesterday';
        if (diffInDays <= 7) return `${diffInDays} days ago`;
        if (diffInDays <= 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
        return this.formatDate(dateString);
      } catch (error) {
        return dateString;
      }
    }
  
    getWeekNumber(date) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
      const week1 = new Date(d.getFullYear(), 0, 4);
      return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
    }
  
    // =====================================================
    // ANALYTICS AND INSIGHTS
    // =====================================================
  
    calculateStreakData(checkins) {
      if (!checkins || checkins.length === 0) {
        return { currentStreak: 0, longestStreak: 0, streakDates: [] };
      }
  
      // Sort checkins by date (most recent first)
      const sortedCheckins = [...checkins].sort((a, b) => 
        new Date(b.checkin_week) - new Date(a.checkin_week)
      );
  
      let currentStreak = 0;
      let longestStreak = 0;
      let tempStreak = 0;
      const streakDates = [];
  
      // Calculate current streak (from most recent)
      const today = new Date();
      const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  
      for (let i = 0; i < sortedCheckins.length; i++) {
        const checkinDate = new Date(sortedCheckins[i].checkin_week);
        
        if (i === 0) {
          // First checkin - check if it's recent enough
          if (checkinDate >= oneWeekAgo) {
            currentStreak = 1;
            tempStreak = 1;
            streakDates.push(checkinDate);
          }
        } else {
          const prevCheckinDate = new Date(sortedCheckins[i - 1].checkin_week);
          const daysDiff = Math.abs((prevCheckinDate - checkinDate) / (1000 * 60 * 60 * 24));
          
          // If within 2 weeks, continue streak
          if (daysDiff <= 14) {
            if (currentStreak > 0) currentStreak++;
            tempStreak++;
            streakDates.push(checkinDate);
          } else {
            // Streak broken
            longestStreak = Math.max(longestStreak, tempStreak);
            if (currentStreak === 0) tempStreak = 1;
            else currentStreak = 0;
          }
        }
      }
  
      longestStreak = Math.max(longestStreak, tempStreak);
  
      return { currentStreak, longestStreak, streakDates };
    }
  
    getGoalInsights(memberData, checkins) {
      if (!memberData || !checkins) return null;
  
      const { days_until_target } = memberData;
      const recentCheckins = checkins.slice(0, 4); // Last 4 check-ins
      
      let insights = [];
  
      // Time pressure insights
      if (days_until_target < 30) {
        insights.push({
          type: 'urgency',
          message: `🕒 Only ${days_until_target} days left! Focus on your highest-impact activities.`,
          priority: 'high'
        });
      } else if (days_until_target < 90) {
        insights.push({
          type: 'progress',
          message: `⏰ ${days_until_target} days remaining. You're in the final stretch!`,
          priority: 'medium'
        });
      }
  
      // Progress pattern insights
      if (recentCheckins.length >= 3) {
        const ratings = recentCheckins.map(c => c.progress_rating).filter(Boolean);
        if (ratings.length >= 3) {
          const trend = this.calculateTrend(ratings);
          if (trend > 0.5) {
            insights.push({
              type: 'success',
              message: '📈 Your progress is accelerating! Keep up the momentum.',
              priority: 'positive'
            });
          } else if (trend < -0.5) {
            insights.push({
              type: 'support',
              message: '💪 Consider reaching out to your accountability group for support.',
              priority: 'medium'
            });
          }
        }
      }
  
      return insights;
    }
  
    calculateTrend(values) {
      if (values.length < 2) return 0;
      
      const first = values[values.length - 1];
      const last = values[0];
      return (last - first) / values.length;
    }
  
    // =====================================================
    // CLEANUP AND INITIALIZATION
    // =====================================================
  
    // Initialize service (called when component mounts)
    async initialize() {
      console.log('🔑 Initializing Member Service...');
      
      const token = this.initializeToken();
      if (token) {
        console.log('🔑 Found existing token, validating...');
        const validation = await this.validateToken();
        if (!validation.success) {
          console.log('🔑 Token validation failed, clearing');
          this.clearToken();
        }
      }
      
      return this.isTokenValid();
    }
  
    // Cleanup (called when component unmounts)
    cleanup() {
      this.stopAutoRefresh();
      // Keep token and cache for potential return visits
      console.log('🔑 Member Service cleaned up');
    }
  }
  
  // Create and export singleton instance
  const memberService = new MemberService();
  export default memberService;